import PropTypes from 'prop-types';

import { styled } from '@mui/material/styles';
import {
  Box,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CloseIcon,
  CloseIconThin,
  SettingIcon,
  GlobalIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { CheckBox } from '@classity-edtech/ui-base';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { ArrowLeftIcon } from '@mui/x-date-pickers';

export interface LeftSidebarFilterProps {
  openSidebar: boolean;
  toggleOpenSidebar: () => void;
  gradeLevels: {
    secondary: { id: string; label: string }[];
    primary: { id: string; label: string }[];
  };
  selectedGradeLevels: string[];
  toggleGradeLevel: (gradeId: string) => void;
  selectAllGradeLevels: (category: 'secondary' | 'primary') => void;
  timeOfDayOptions: { id: string; label: string }[];
  selectedTimeOfDay: string[];
  toggleTimeOfDay: (timeId: string) => void;
  selectAllTimeOfDay: () => void;
  expandedSections: {
    secondary: boolean;
    primary: boolean;
    timeOfDay: boolean;
  };
  toggleSection: (section: 'secondary' | 'primary' | 'timeOfDay') => void;
}

export const LeftSidebarFilter = ({
  openSidebar,
  toggleOpenSidebar,
  gradeLevels,
  selectedGradeLevels,
  toggleGradeLevel,
  selectAllGradeLevels,
  timeOfDayOptions,
  selectedTimeOfDay,
  toggleTimeOfDay,
  selectAllTimeOfDay,
  expandedSections,
  toggleSection,
}: LeftSidebarFilterProps) => {
  return (
    <Box
      sx={{
        position: { xs: 'absolute', lg: 'sticky' },
        top: { xs: 0, lg: '82px' },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: 'fit-content',
        backgroundColor: globalColours['$global-colour-background-elevated'],
        zIndex: 1001,
        pl: { xs: 2, md: 4 },
        py: { xs: 2, md: 0 },
        pr: { xs: 2, md: 0 },
        minWidth: { xs: '100%', md: '180px' },
        left: { xs: openSidebar ? 0 : '-100%', lg: 0 },
        transition: 'left 0.3s ease-in-out',
      }}
    >
      <Box
        sx={{ ml: 'auto', width: 'fit-content', display: { xs: 'block', lg: 'none' } }}
        onClick={toggleOpenSidebar}
      >
        <CloseIcon sx={{ width: '24px', height: '24px', cursor: 'pointer' }} />
      </Box>

      <Accordion
        expanded={expandedSections.secondary}
        onChange={() => toggleSection('secondary')}
        disableGutters
        elevation={0}
        square
        sx={{
          borderTop: `solid 0px transparent`,
          bgcolor: 'transparent',
          '& .MuiButtonBase-root': {
            px: 0,
          },
          '&::before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: globalColours['$global-colour-interactive-primary-default'],
              cursor: 'pointer',
            }}
          >
            <Typography variant='body2SemiBold'>Secondary school</Typography>

            <ArrowLeftIcon
              sx={{
                width: '16px',
                height: '16px',
                transform: expandedSections.secondary ? 'rotate(90deg)' : 'rotate(-90deg)',
              }}
            />
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 0,
            py: 0,
          }}
        >
          <Stack sx={{ gap: 1 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => selectAllGradeLevels('secondary')}
            >
              <CheckBox
                checked={gradeLevels.secondary.every(g => selectedGradeLevels.includes(g.id))}
                indeterminate={
                  gradeLevels.secondary.some(g => selectedGradeLevels.includes(g.id)) &&
                  !gradeLevels.secondary.every(g => selectedGradeLevels.includes(g.id))
                }
                isSecondary
              />
              <Typography variant='body3SemiBold'>Select all</Typography>
            </Box>
            {gradeLevels.secondary.map(grade => (
              <Box
                key={grade.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  ml: 0.5,
                }}
                onClick={() => toggleGradeLevel(grade.id)}
              >
                <CheckBox checked={selectedGradeLevels.includes(grade.id)} isSecondary />
                <Typography variant='body3'>{grade.label}</Typography>
              </Box>
            ))}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => selectAllGradeLevels('secondary')}
            >
              <CloseIconThin sx={{ width: '14px', height: '14px' }} />
              <Typography variant='body3'>Clear all</Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedSections.primary}
        onChange={() => toggleSection('primary')}
        disableGutters
        elevation={0}
        square
        sx={{
          borderTop: `solid 0px transparent`,
          bgcolor: 'transparent',
          '& .MuiButtonBase-root': {
            px: 0,
          },
          '&::before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: globalColours['$global-colour-interactive-primary-default'],
              cursor: 'pointer',
            }}
          >
            <Typography variant='body2SemiBold'>Primary school</Typography>

            <ArrowLeftIcon
              sx={{
                width: '16px',
                height: '16px',
                transform: expandedSections.primary ? 'rotate(90deg)' : 'rotate(-90deg)',
              }}
            />
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 0,
            py: 0,
          }}
        >
          <Stack sx={{ gap: 1 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => selectAllGradeLevels('primary')}
            >
              <CheckBox
                checked={gradeLevels.primary.every(g => selectedGradeLevels.includes(g.id))}
                indeterminate={
                  gradeLevels.primary.some(g => selectedGradeLevels.includes(g.id)) &&
                  !gradeLevels.primary.every(g => selectedGradeLevels.includes(g.id))
                }
                isSecondary
              />
              <Typography variant='body3SemiBold'>Select all</Typography>
            </Box>
            {gradeLevels.primary.map(grade => (
              <Box
                key={grade.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  ml: 0.5,
                }}
                onClick={() => toggleGradeLevel(grade.id)}
              >
                <CheckBox checked={selectedGradeLevels.includes(grade.id)} isSecondary />
                <Typography variant='body3'>{grade.label}</Typography>
              </Box>
            ))}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => selectAllGradeLevels('primary')}
            >
              <CloseIconThin sx={{ width: '14px', height: '14px' }} />
              <Typography variant='body3'>Clear all</Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedSections.timeOfDay}
        onChange={() => toggleSection('timeOfDay')}
        disableGutters
        elevation={0}
        square
        sx={{
          borderTop: `solid 0px transparent`,
          bgcolor: 'transparent',
          '& .MuiButtonBase-root': {
            px: 0,
          },
          '&::before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: globalColours['$global-colour-interactive-primary-default'],
              cursor: 'pointer',
            }}
          >
            <Typography variant='body2SemiBold'>Time of day</Typography>

            <ArrowLeftIcon
              sx={{
                width: '16px',
                height: '16px',
                transform: expandedSections.timeOfDay ? 'rotate(90deg)' : 'rotate(-90deg)',
              }}
            />
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 0,
            py: 0,
          }}
        >
          <Stack sx={{ gap: 1 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={selectAllTimeOfDay}
            >
              <CheckBox
                checked={timeOfDayOptions.every(t => selectedTimeOfDay.includes(t.id))}
                indeterminate={
                  timeOfDayOptions.some(t => selectedTimeOfDay.includes(t.id)) &&
                  !timeOfDayOptions.every(t => selectedTimeOfDay.includes(t.id))
                }
                isSecondary
              />
              <Typography variant='body3SemiBold'>Select all</Typography>
            </Box>
            {timeOfDayOptions.map(timeOption => (
              <Box
                key={timeOption.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  ml: 0.5,
                }}
                onClick={() => toggleTimeOfDay(timeOption.id)}
              >
                <CheckBox checked={selectedTimeOfDay.includes(timeOption.id)} isSecondary />
                <Typography variant='body3'>{timeOption.label}</Typography>
              </Box>
            ))}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => selectAllTimeOfDay()}
            >
              <CloseIconThin sx={{ width: '14px', height: '14px' }} />
              <Typography variant='body3'>Clear all</Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Stack
        sx={{
          pt: 2,
          borderTop: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          gap: 1,
          my: 5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
          <SettingIcon sx={{ width: '16px', height: '16px' }} />
          <Typography variant='body3'>Settings</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
          <GlobalIcon sx={{ width: '16px', height: '16px' }} />
          <Typography variant='body3'>Nigeria</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

LeftSidebarFilter.propTypes = {
  openSidebar: PropTypes.bool.isRequired,
  toggleOpenSidebar: PropTypes.func.isRequired,
  gradeLevels: PropTypes.object.isRequired,
  selectedGradeLevels: PropTypes.array.isRequired,
  toggleGradeLevel: PropTypes.func.isRequired,
  selectAllGradeLevels: PropTypes.func.isRequired,
  timeOfDayOptions: PropTypes.array.isRequired,
  selectedTimeOfDay: PropTypes.array.isRequired,
  toggleTimeOfDay: PropTypes.func.isRequired,
  selectAllTimeOfDay: PropTypes.func.isRequired,
  expandedSections: PropTypes.object.isRequired,
  toggleSection: PropTypes.func.isRequired,
};

export default styled<any>(LeftSidebarFilter)(
  ({ theme }) => `
`,
);
